import React, { useState } from "react";
import linuxVid from "../../assets/vid/network/agent-linux.mp4";
import windowsVid from "../../assets/vid/network/agent-win.mp4";
import { FaWindows, FaLinux, FaApple } from "react-icons/fa";
import { useThemeMode } from "../../context/contextTheme.js";
import hexToRGBA from "../../utils/hexToRGBA.js";

const InstallSteps = () => {
  const { theme } = useThemeMode();
  const [activeTab, setActiveTab] = useState("windows");

  const tabs = [
    { id: "windows", label: "Windows", icon: <FaWindows />, video: windowsVid },
    { id: "linux", label: "Linux", icon: <FaLinux />, video: linuxVid },
    { id: "darwin", label: "macOS", icon: <FaApple />, video: null },
  ];

  return (
    <div
      className="card border-0 rounded-4 shadow-sm overflow-hidden"
      style={{
        width: 380, height: 'auto',
        background: theme.background,
        border: `1px solid ${hexToRGBA(theme.textColor, 0.1)}`,
      }}
    >
      {/* Header Area */}
      <div className="px-3 py-3 border-bottom">
        <h6 className="fw-bold mb-1" style={{ color: theme.textColor }}>
          Installation Guide
        </h6>
        <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
          Follow the setup steps for your operating system.
        </p>
      </div>

      {/* OS Tab Selector */}
      <div className="d-flex p-2 bg-light border-bottom" style={{ height: 40 }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="btn flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-2 rounded-2 border-0 transition-all"
            style={{
              fontSize: "0.8rem",
              fontWeight: activeTab === tab.id ? "700" : "500",
              background: activeTab === tab.id ? "white" : "transparent",
              color: activeTab === tab.id ? theme.primary : theme.textMuted,
              boxShadow: activeTab === tab.id ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="p-3">
        {tabs.find((t) => t.id === activeTab)?.video ? (
          <div className="position-relative rounded-3 overflow-hidden bg-black shadow-inner">
            <video
              src={tabs.find((t) => t.id === activeTab).video}
              controls
              autoPlay
              loop
              muted
              className="w-100 d-block"
              style={{ maxHeight: "200px", objectFit: "cover" }}
            />
            <div
              className="position-absolute top-0 start-0 p-2"
              style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)", width: "100%" }}
            >
              <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '0.6rem' }}>
                LIVE TUTORIAL
              </span>
            </div>
          </div>
        ) : (
          <div
            className="d-flex flex-column py-3 px-3 rounded-3 border border-dashed"
            style={{ borderColor: hexToRGBA(theme.textColor, 0.2) }}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <FaApple size={24} style={{ color: theme.primary }} />
              <span className="fw-bold" style={{ color: theme.textColor }}>
                Apple Silicon setup
              </span>
            </div>
            <ol
              className="mb-0 ps-3"
              style={{ color: theme.textMuted, fontSize: "0.72rem", lineHeight: 1.55 }}
            >
              <li>
                Install native nmap with <code>brew install nmap</code>.
              </li>
              <li>Extract the downloaded ZIP and move SAVE Agent to Applications.</li>
              <li>
                Control-click SAVE Agent, choose <strong>Open</strong>, then approve
                local-network access when macOS asks.
              </li>
            </ol>
          </div>
        )}

        {/* Dynamic Footnote based on Tab */}
        <div className="mt-3 p-2 rounded-2" style={{ backgroundColor: hexToRGBA(theme.textColor, 0.03) }}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <i className="bi bi-info-circle-fill text-primary" style={{ fontSize: '0.8rem' }}></i>
            <span className="fw-bold" style={{ fontSize: '0.75rem' }}>Pro Tip</span>
          </div>
          <p className="mb-0 text-muted" style={{ fontSize: '0.7rem', lineHeight: '1.4' }}>
            {activeTab === 'windows' && "Ensure you run the Save-Agent.exe with Administrator privileges for deep packet inspection."}
            {activeTab === 'linux' && "If the binary fails to launch, verify executable permissions using 'chmod +x'."}
            {activeTab === 'darwin' && "This local development build is ad-hoc signed. If Gatekeeper blocks its first launch, Control-click the app and choose Open, or use System Settings > Privacy & Security > Open Anyway."}
          </p>
        </div>
      </div>

      <style>{`
        .transition-all { transition: all 0.2s ease-in-out; }
        .shadow-inner { box-shadow: inset 0 2px 10px rgba(0,0,0,0.2); }
      `}</style>
    </div>
  );
};

export default InstallSteps;
